import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  ReactNode
} from 'react';

import { CropOrientation } from './Canvas/Canvas.types';

interface IPEGxPageProvider {
  children: JSX.Element | ReactNode[] | null;
}

enum ActionType {
  SET_SELECTED_PAGES = 'SET_SELECTED_PAGES',
  SET_ACTIVE_POINT = 'SET_ACTIVE_POINT'
}

type Action = {
  type: ActionType.SET_SELECTED_PAGES | ActionType.SET_ACTIVE_POINT;
  payload: any;
};

type State = {
  selectedPages: Array<number>;
  activePoint: CropOrientation;
};

type Context = {
  selectedPages: Array<number>;
  activePoint: CropOrientation;
  setSelectedPages: (pageNum: number) => void;
  resetSelectedPages: () => void;
  setAllPages: (numPages: number) => void;
  setActivePoint: (activePoint: CropOrientation) => void;
};

export const PEGxPageContext = createContext<Context>(undefined!);

const initState = {
  selectedPages: [],
  activePoint: CropOrientation.DEFAULT,
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case ActionType.SET_SELECTED_PAGES: {
      return {
        ...state,
        selectedPages: action.payload,
      };
    }
    case ActionType.SET_ACTIVE_POINT: {
      return {
        ...state,
        activePoint: action.payload,
      };
    }
    default:
      return initState;
  }
}

export function PEGxPageProvider({ children }: IPEGxPageProvider) {
  const [state, dispatch] = useReducer(reducer, initState);
  const { selectedPages, activePoint } = state;

  const setSelectedPages = (numPage: number) => {
    const newSelectedPages = selectedPages.includes(numPage)
      ? selectedPages.filter((p: number) => p !== numPage)
      : selectedPages.concat(numPage).sort((a: number, b: number) => a > b ? 1 : -1);

    dispatch({
      type: ActionType.SET_SELECTED_PAGES,
      payload: newSelectedPages,
    });
  };

  const resetSelectedPages = useCallback(() => {
    dispatch({
      type: ActionType.SET_SELECTED_PAGES,
      payload: [],
    });
  }, [dispatch, selectedPages]);

  const setAllPages = useCallback((numPages: number) => {
    dispatch({
      type: ActionType.SET_SELECTED_PAGES,
      payload: numPages === selectedPages.length
        ? []
        : Array.from(Array(numPages), (_, x) => x + 1),
    });
  }, [dispatch, selectedPages]);

  const setActivePoint = useCallback((point) => {
    dispatch({
      type: ActionType.SET_ACTIVE_POINT,
      payload: point,
    });
  }, [dispatch, activePoint]);

  const memoizedValue = useMemo(
    () => ({
      selectedPages,
      setSelectedPages,
      resetSelectedPages,
      activePoint,
      setAllPages,
      setActivePoint
    }),
    [
      selectedPages,
      setSelectedPages,
      resetSelectedPages,
      activePoint,
      setAllPages,
      setActivePoint
    ],
  );

  return <PEGxPageContext.Provider value={memoizedValue}>{children}</PEGxPageContext.Provider>;
}

const usePEGxPog = () => useContext(PEGxPageContext);

export default usePEGxPog;
